<template>
  <avs-card class="pb-4 donation-list-container">
    <!-- ===== HEADER / TOOLBAR ===== -->
    <template v-slot:title>
      <!-- Desktop -->
      <div v-if="$vuetify.breakpoint.mdAndUp">
        <v-container fluid style="padding: 0;">
          <v-row class="g-2">
            <v-col cols="12" md="2">
              <v-text-field
                v-model="search"
                @input="onSearchChange"
                clearable
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-magnify"
                :label="$t('commons.search')"
                class="search-field"
              />
            </v-col>

            <v-col cols="12" md="2">
              <v-select
                v-model="statusFilter"
                :items="statusOptions"
                item-text="text"
                item-value="value"
                clearable
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-filter"
                :label="$t('Filtrer par statut')"
                @change="applyFilters"
              />
            </v-col>

            <v-col cols="12" md="2" v-if="productTypeOptions.length">
              <v-select
                v-model="productType"
                :items="productTypeOptions"
                item-text="text"
                item-value="value"
                clearable
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-shape"
                :label="$t('Type de produit')"
                @change="onProductTypeChange"
              />
            </v-col>

            <v-col cols="12" md="2">
              <v-autocomplete
                v-model="productId"
                :items="products"
                item-text="name"
                item-value="id"
                clearable
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-gas-station"
                :label="$t('Produit')"
                @change="applyFilters"
              />
            </v-col>

            <v-col cols="12" md="2">
              <v-menu
                v-model="menuStartDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="230px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="startDate"
                    :label="$t('Date de début')"
                    prepend-inner-icon="mdi-calendar-start"
                    outlined
                    dense
                    hide-details
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="startDate" @input="applyFilters" color="primary" />
              </v-menu>
            </v-col>

            <v-col cols="12" md="2">
              <v-menu
                v-model="menuEndDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="230px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="endDate"
                    :label="$t('Date de fin')"
                    prepend-inner-icon="mdi-calendar-end"
                    outlined
                    dense
                    hide-details
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="endDate" @input="applyFilters" color="primary" />
              </v-menu>
            </v-col>

            <v-spacer />

            <v-col cols="12" md="2" class="d-flex align-center justify-end">
              <avs-createButton
                v-if="canCreateDonation"
                :spantext="$t('actions.create', { entity: $t('Don').toLowerCase() })"
                menu
                :name="$t('actions.new', { entity: $t('Don').toLowerCase() })"
                @click="handleOpenEditDialog()"
              />
              <v-chip v-else small color="warning" text-color="white">
                <v-icon left x-small>mdi-lock</v-icon>
                {{ $t('Dons désactivés') }}
              </v-chip>
            </v-col>
          </v-row>

          <!-- Statistiques / Totaux -->
          <v-row class="stats-section">
            <v-col cols="12">
              <div class="stats-wrapper">
                <div class="stats-container">
                  <!-- Carte Total Général -->
                  <v-card class="stat-card stat-card-main" elevation="4">
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <div class="stat-label">Montant total des dons</div>
                        <div class="stat-value">
                          {{ formatNumber(totals.amount) }} <span class="stat-unit">XOF</span>
                        </div>
                      </div>
                      <div class="stat-icon-wrapper">
                        <v-icon size="40" color="white">mdi-cash-multiple</v-icon>
                      </div>
                    </div>
                    <v-divider class="my-0" style="border-color: rgba(255,255,255,0.3);" />
                    <div class="stat-secondary mt-2">
                      <v-icon small color="white">mdi-gauge</v-icon>
                      {{ formatNumber(totals.liters) }} L
                    </div>
                  </v-card>

                  <!-- Totaux par produit -->
                  <v-card
                    v-for="(p, index) in totalByProduct"
                    :key="p.id || index"
                    class="stat-card stat-card-product bgTable"
                    elevation="3"
                  >
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <div class="stat-label-product">{{ p.name }}</div>
                        <div class="stat-value-product">
                          {{ formatNumber(p.total_amount) }} <span class="stat-unit-product">XOF</span>
                        </div>
                      </div>
                      <div class="stat-icon-wrapper-product">
                        <v-icon size="28">mdi-barrel</v-icon>
                      </div>
                    </div>
                    <div class="stat-secondary-product">
                      <v-icon x-small>mdi-gauge</v-icon>
                      {{ formatNumber(p.total_liters) }} L
                    </div>
                  </v-card>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </div>

      <!-- Mobile -->
      <div v-else class="col-12 mobile-header px-4 pt-4 pb-2">
        <!-- Bouton nouveau don -->
        <div class="d-flex align-center justify-end mb-3">
          <avs-createButton
            v-if="canCreateDonation"
            :spantext="$t('actions.create', { entity: $t('Don').toLowerCase() })"
            :name="$t('actions.new', { entity: $t('Don').toLowerCase() })"
            @click="handleOpenEditDialog()"
          />
          <v-chip v-else small color="warning" text-color="white">
            <v-icon left x-small>mdi-lock</v-icon>
            {{ $t('Dons désactivés') }}
          </v-chip>
        </div>

        <!-- Filtres dans un expander -->
        <v-expansion-panels flat class="mb-3">
          <v-expansion-panel>
            <v-expansion-panel-header>
              <div class="d-flex align-center">
                <v-icon small class="mr-2">mdi-filter-variant</v-icon>
                {{ $t('Filtres') }}
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-text-field
                v-model="search"
                @input="onSearchChange"
                clearable
                dense
                outlined
                hide-details
                prepend-inner-icon="mdi-magnify"
                :label="$t('commons.search')"
                class="mb-3"
              />
              <v-select
                v-model="statusFilter"
                :items="statusOptions"
                item-text="text"
                item-value="value"
                clearable
                dense
                outlined
                hide-details
                prepend-inner-icon="mdi-filter"
                :label="$t('Filtrer par statut')"
                class="mb-3"
                @change="applyFilters"
              />
              <v-select
                v-if="productTypeOptions.length"
                v-model="productType"
                :items="productTypeOptions"
                item-text="text"
                item-value="value"
                clearable
                dense
                outlined
                hide-details
                prepend-inner-icon="mdi-shape"
                :label="$t('Type de produit')"
                class="mb-3"
                @change="onProductTypeChange"
              />
              <v-autocomplete
                v-model="productId"
                :items="products"
                item-text="name"
                item-value="id"
                clearable
                dense
                outlined
                hide-details
                prepend-inner-icon="mdi-gas-station"
                :label="$t('Produit')"
                class="mb-3"
                @change="applyFilters"
              />

              <v-menu v-model="menuStartDate" :close-on-content-click="false" transition="scale-transition" offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="startDate"
                    :label="$t('Date de début')"
                    prepend-inner-icon="mdi-calendar-start"
                    outlined dense hide-details readonly
                    v-bind="attrs" v-on="on" class="mb-3"
                  />
                </template>
                <v-date-picker v-model="startDate" @input="applyFilters" />
              </v-menu>

              <v-menu v-model="menuEndDate" :close-on-content-click="false" transition="scale-transition" offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="endDate"
                    :label="$t('Date de fin')"
                    prepend-inner-icon="mdi-calendar-end"
                    outlined dense hide-details readonly
                    v-bind="attrs" v-on="on" class="mb-3"
                  />
                </template>
                <v-date-picker v-model="endDate" @input="applyFilters" />
              </v-menu>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </template>

    <!-- ===== CONTENU ===== -->
    <template v-slot:content>
      <!-- Desktop: tableau -->
      <v-data-table
        v-if="$vuetify.breakpoint.mdAndUp"
        class="ma-6 bgTable"
        dense
        :headers="headers"
        hide-default-footer
        :no-data-text="$t('messages.information.no_data')"
        :loading-text="$t('Chargement des données')"
        :items="items"
        :items-per-page="-1"
        :search="search"
        item-key="id"
        :loading="isLoading"
        @dblclick:row="(_, { item }) => tryOpenEdit(item)"
      >
        <!-- Bénéficiaire -->
        <template v-slot:item.beneficiary="{ item }">
          <div class="d-flex flex-column">
            <div class="font-weight-bold">
              {{ (item.last_name || '') + ' ' + (item.first_name || '') }}
            </div>
            <div class="grey--text text--darken-1 text-caption">
              {{ item.phone_number || '—' }}
            </div>
          </div>
        </template>

        <template v-slot:item.company_product_name="{ item }">
          <v-chip small label color="accent" text-color="white">
            <v-icon left x-small>mdi-gas-station</v-icon>
            {{ item.company_product_name || '—' }}
          </v-chip>
        </template>

        <template v-slot:item.purchase_liters="{ item }">
          {{ formatNumber(item.purchase_liters) }}
        </template>

        <template v-slot:item.progress="{ item }">
          <div>
            <div class="d-flex justify-space-between mb-1 text-caption">
              <span>{{ formatNumber(item.liter_remaining || 0) }} / {{ formatNumber(item.purchase_liters || 0) }} L ({{ progressPercent(item) }}%)</span>
            </div>
            <v-progress-linear
              :value="progressPercent(item)"
              height="10"
              :color="progressColor(item)"
              rounded
            />
          </div>
        </template>

        <template v-slot:item.purchase_amount="{ item }">
          {{ formatNumber(item.purchase_amount) }} XOF
        </template>

        <template v-slot:item.created_at="{ item }">
          {{ toDatetime(item.created_at) }}
        </template>

        <template v-slot:item.valid_until="{ item }">
          {{ toDatetime(item.valid_until) }}
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip small :color="statusColor(item)" text-color="white" class="text-capitalize">
            {{ statusLabel(item) }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-center align-center">
            <v-btn
              v-if="canValidate(item)"
              icon
              color="success"
              :title="$t('Valider ce don')"
              @click.stop="askValidate(item)"
            >
              <v-icon>mdi-check-circle-outline</v-icon>
            </v-btn>
            <avs-editButton :disabled="!canEdit(item)" @click.stop="tryOpenEdit(item)" />
            <div class="mx-2" />
            <div class="d-flex justify-center mx-1">
              <template v-if="downloadingId === item.id">
                <v-progress-circular indeterminate small />
              </template>
              <template v-else>
                <avs-button
                  icon color="secondary" :iconI="'mdi-download-outline'"
                  label fab small
                  :spantext="$t('Télécharger la carte')"
                  @click.stop="downloadBadge(item)"
                />
              </template>
            </div>
            <avs-deleteButton :disabled="!canDelete(item)" @click.stop="tryOpenDelete(item)" />
          </div>
        </template>
      </v-data-table>

      <!-- Mobile : cartes -->
      <div v-else class="px-4 pt-2">
        <v-alert
          v-if="!items.length && !isLoading"
          type="info"
          dense
          outlined
          class="mb-2"
        >
          {{ $t('messages.information.no_data') }}
        </v-alert>

        <v-skeleton-loader
          v-else-if="isLoading && !items.length"
          type="image, article, actions"
          class="mb-3"
        />

        <v-card
          v-for="it in items"
          :key="it.id"
          class="mb-3 donation-card"
          outlined
          @click="tryOpenEdit(it)"
        >
          <v-card-title class="py-3 d-flex align-center">
            <div class="title-wrap">
              <div class="font-weight-bold text-truncate">
                {{ (it.last_name || '') + ' ' + (it.first_name || '') }}
              </div>
              <div class="grey--text text--darken-1 text-caption">
                {{ it.phone_number || '—' }}
              </div>
            </div>
            <v-spacer />
            <v-chip small :color="statusColor(it)" text-color="white" class="text-capitalize">
              {{ statusLabel(it) }}
            </v-chip>
          </v-card-title>

          <v-divider />

          <v-card-text class="py-3">
            <div class="grid-2">
              <div class="cell">
                <div class="label">{{ $t('Produit') }}</div>
                <div class="value">{{ it.company_product_name || '—' }}</div>
              </div>

              <div class="cell">
                <div class="label">{{ $t('Site') }}</div>
                <div class="value">{{ it.company_shop_name || '—' }}</div>
              </div>

              <div class="cell">
                <div class="label">{{ $t('Litres') }}</div>
                <div class="value">{{ formatNumber(it.purchase_liters) }}</div>
              </div>

              <div class="cell">
                <div class="label">{{ $t('Montant') }}</div>
                <div class="value">{{ formatNumber(it.purchase_amount) }} XOF</div>
              </div>

              <div class="cell full-width">
                <div class="label">{{ $t('Restant') }}</div>
                <div class="d-flex justify-space-between text-caption">
                  <span>{{ formatNumber(it.liter_remaining || 0) }} / {{ formatNumber(it.purchase_liters || 0) }} L</span>
                  <span>{{ progressPercent(it) }}%</span>
                </div>
                <v-progress-linear
                  :value="progressPercent(it)"
                  height="10"
                  :color="progressColor(it)"
                  rounded
                />
              </div>

              <div class="cell">
                <div class="label">{{ $t('Date de création') }}</div>
                <div class="value">{{ toDatetime(it.created_at) }}</div>
              </div>
              <div class="cell">
                <div class="label">{{ $t('Date de validité') }}</div>
                <div class="value">{{ toDatetime(it.valid_until) }}</div>
              </div>
            </div>
          </v-card-text>

          <v-divider />

          <v-card-actions class="py-2">
            <div class="d-flex align-center">
              <v-btn icon color="secondary" :title="$t('Télécharger la carte')" class="mx-1" @click.stop="downloadBadge(it)">
                <v-icon small>mdi-download-outline</v-icon>
              </v-btn>
              <v-btn icon color="primary" :title="$t('Editer')" class="mx-1" :disabled="!canEdit(it)" @click.stop="tryOpenEdit(it)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon color="error" :title="$t('Supprimer')" class="mx-1" :disabled="!canDelete(it)" @click.stop="tryOpenDelete(it)">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </div>
          </v-card-actions>
        </v-card>
      </div>

      <!-- Dialog édition -->
      <v-dialog v-model="editDialogIsOpen" :fullscreen="$vuetify.breakpoint.smAndDown" :width="$vuetify.breakpoint.smAndDown ? '100vw' : '40vw'" scrollable transition="dialog-bottom-transition">
        <component
          v-if="editDialogContent"
          v-model="selectedItem"
          :is="editDialogContent"
          :hasUniqueName="hasUniqueName"
          :isEditable="isEditable"
          @cancel="handleCancel"
          @error="handleError"
          @success="handleSuccess"
        />
      </v-dialog>

      <!-- Dialog validation/refus interne -->
      <v-dialog v-model="validateDialogIsOpen" max-width="480" persistent>
        <v-card v-if="validateTarget">
          <v-card-title class="text-h6">
            <v-icon left color="success">mdi-check-circle-outline</v-icon>
            {{ $t("Valider ce don ?") }}
          </v-card-title>
          <v-card-text>
            <p class="mb-2">
              <strong>{{ validateTarget.last_name }} {{ validateTarget.first_name }}</strong>
              <span v-if="validateTarget.phone_number"> ({{ validateTarget.phone_number }})</span>
            </p>
            <p class="mb-1">
              <strong>{{ $t("Produit") }} :</strong>
              {{ validateTarget.company_product_name || '—' }}
            </p>
            <p class="mb-1">
              <strong>{{ $t("Quantité") }} :</strong>
              {{ formatNumber(validateTarget.purchase_liters) }} L
              ({{ formatNumber(validateTarget.purchase_amount) }} XOF)
            </p>
            <p class="text-caption grey--text mt-3">
              {{ $t("La validation consommera le plafond de la société.") }}
            </p>
          </v-card-text>
          <v-card-actions>
            <v-btn
              outlined
              color="error"
              :loading="validateActing && validateMode === 'reject'"
              :disabled="validateActing && validateMode !== 'reject'"
              @click="confirmReject"
            >
              <v-icon left>mdi-close-circle-outline</v-icon>
              {{ $t("Rejeter") }}
            </v-btn>
            <v-spacer />
            <v-btn text :disabled="validateActing" @click="validateDialogIsOpen = false">
              {{ $t("Annuler") }}
            </v-btn>
            <v-btn
              color="success"
              depressed
              :loading="validateActing && validateMode === 'approve'"
              :disabled="validateActing && validateMode !== 'approve'"
              @click="confirmApprove"
            >
              <v-icon left>mdi-check</v-icon>
              {{ $t("Valider") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <DeleteDialog
        v-model="deleteDialogIsOpen"
        @cancel="handleCancel"
        @delete="handleDelete"
        resource="Don"
      />
    </template>
  </avs-card>
</template>

<script>
import DeleteDialog from "@/components/generics/dialogs/DeleteDialog.vue";
import DonationEditor from "@/components/ri3/donation/DonationEditor.vue";
import { DonationDetailService, ProductDetailService } from "@/services";
import { mkUnique } from "@/utils/validator";
import { toDatetime } from "@/utils/datetime";
import { debounce } from "lodash";

export default {
  name: "DonationList",
  components: { DeleteDialog, DonationEditor },
  data() {
    const today = new Date();
    const fd = new Date(today.getFullYear(), today.getMonth(), 1);
    const ld = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    return {
      search: "",
      items: [],
      isLoading: false,

      // Filtres
      statusFilter: "unused",
      statusOptions: [
        { text: this.$t("Non utilisé"), value: "unused" },
        { text: this.$t("En cours"), value: "ongoing" },
        { text: this.$t("Expiré"), value: "expired" },
      ],
      productType: null,
      productTypeOptions: [],
      productId: null,
      products: [],
      startDate: this.formatDateStr(fd),
      endDate: this.formatDateStr(ld),
      menuStartDate: false,
      menuEndDate: false,

      // Totaux
      totals: { amount: 0, liters: 0, count: 0 },
      totalByProduct: [],

      // Table
      headers: [
        { text: this.$t("Bénéficiaire"), value: "beneficiary" },
        { text: this.$t("Produit"), value: "company_product_name", align: "center" },
        { text: this.$t("Site"), value: "company_shop_name" },
        { text: this.$t("Litres"), value: "purchase_liters", align: "right" },
        { text: this.$t("Restant (L)"), value: "progress", align: "left", sortable: false },
        { text: this.$t("Montant"), value: "purchase_amount", align: "right" },
        { text: this.$t("Date de création"), value: "created_at" },
        { text: this.$t("Date de validité"), value: "valid_until" },
        { text: this.$t("Statut"), value: "status", align: "center" },
        { text: "", value: "actions", align: "center", sortable: false },
      ],

      // Dialogs
      editDialogIsOpen: false,
      editDialogContent: null,
      deleteDialogIsOpen: false,
      selectedItem: null,
      isEditable: true,

      // Validation interne
      validateDialogIsOpen: false,
      validateTarget: null,
      validateActing: false,
      validateMode: "approve", // approve | reject

      // UI
      downloadingId: null,
      getListDebounced: null,
    };
  },
  computed: {
    hasUniqueName() {
      const names = this.items
        .filter(e => e.name !== this.selectedItem?.name)
        .map(e => e.name);
      return mkUnique(names, "name");
    },
    donationsEnabled() {
      const c = this.$store.getters["app/getCompany"];
      return !c || c === -1 || c.donations_enabled !== false;
    },
    isAdminRole() {
      const company = this.$store.getters["app/getCompany"];
      const role = company && company.company_role;
      return this.$store.getters["app/isCompanyOwner"]
        || this.$store.getters["app/isUserSuperUser"]
        || role === "administrator"
        || role === "manager";
    },
    privilegeCodes() {
      const c = this.$store.getters["app/userPrivilegeCodes"];
      return c && c.has ? c : new Set();
    },
    canCreateDonation() {
      if (!this.donationsEnabled) return false;
      return this.isAdminRole || this.privilegeCodes.has("create_donation");
    },
    canEditDonation() {
      return this.isAdminRole || this.privilegeCodes.has("edit_donation");
    },
    canDeleteDonation() {
      return this.isAdminRole || this.privilegeCodes.has("delete_donation");
    },
    canValidateDonation() {
      return this.isAdminRole || this.privilegeCodes.has("validate_donation");
    },
  },
  created() {
    this.getListDebounced = debounce(this.fetchList, 400);
    this.fetchProducts();      // charge les produits (et déduit les types si possible)
    this.fetchList();          // charge la liste + totaux
    if (this.$store.state.app.userPrivileges === null) {
      this.$store.dispatch("app/loadUserPrivileges");
    }
  },
  methods: {
    toDatetime,
    // --- Format utils
    formatNumber(value) {
      const n = Number(value || 0);
      if (!Number.isFinite(n)) return "0";
      return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },
    formatDateStr(d) {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    },

    // --- Filtres
    onSearchChange() { this.getListDebounced(); },
    onProductTypeChange() {
      // Filtrer la liste des produits localement par type si on l'a
      if (this.productType) {
        const all = this._allProducts || [];
        this.products = all.filter(p => (p.type || p.product_type) === this.productType);
      } else {
        this.products = this._allProducts || [];
      }
      this.productId = null;
      this.applyFilters();
    },
    applyFilters() { this.fetchList(); },

    // --- Fetch produits + types
    fetchProducts() {
      ProductDetailService.getprincipalAll(this)
        .then(r => {
          const results = r?.results || r || [];
          const mapped = results.map(x => ({
            id: x.id || x.pk,
            name: x.name || x.title || x.label,
            type: x.type || x.product_type || null,
          })).filter(p => p.id && p.name);

          this._allProducts = mapped;
          this.products = mapped;

          // Déduire les types disponibles (si la donnée existe)
          const types = Array.from(new Set(mapped.map(p => p.type).filter(Boolean)));
          this.productTypeOptions = types.map(t => ({ text: String(t), value: t }));
        })
        .catch(() => { this.products = []; this.productTypeOptions = []; });
    },

    // --- Fetch liste + totaux
    fetchList() {
      this.isLoading = true;

      const params = {
        status: (this.statusFilter || "unused"),
        start_date: this.startDate || null,
        end_date: this.endDate || null,
      };
      if (this.productType) params.product_type = this.productType;
      if (this.productId) params.product = this.productId;

      DonationDetailService.getAll(this, params)
        .then((response) => {
          // Supporte deux formats: {results:[...], totals:{...}} ou {results:{results:[...]}, totals:{...}}
          const results = Array.isArray(response?.results)
            ? response.results
            : (response?.results?.results || []);
          const totals = response?.totals || {};

          this.items = results || [];

          this.totals = {
            amount: Number(totals.amount || 0),
            liters: Number(totals.liters || 0),
            count: Number(totals.count || 0),
          };

          this.totalByProduct = (totals.by_product || []).map(p => ({
            id: p.id,
            name: p.name,
            total_amount: Number(p.total_amount || 0),
            total_liters: Number(p.total_liters || 0),
          }));
        })
        .catch(err => {
          if (err?.response?.status === 403) {
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error",
            });
          } else {
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.operation_failed"),
              color: "error",
            });
          }
        })
        .finally(() => { this.isLoading = false; });
    },

    // --- Status / Progress
    r2(v) {
      const n = Number(v);
      return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
    },
    progressPercent(item) {
      const pl = this.r2(item?.purchase_liters);
      const lr = this.r2(item?.liter_remaining);

      if (pl <= 0) return 0;

      // Litres consommés
      let consumed = pl - lr;

      // Sécurité : clamp entre 0 et pl
      if (consumed < 0) consumed = 0;
      if (consumed > pl) consumed = pl;

      const pct = (consumed / pl) * 100;
      return Math.round(Math.max(0, Math.min(100, pct)));
    },

    // 0–50% consommé = vert, 50–80% = jaune, 80–100% = rouge
    progressColor(item) {
      const pct = this.progressPercent(item);
      if (pct <= 50) return "success";
      if (pct <= 80) return "warning";
      return "error";
    },
    isExpired(item) {
      const now = new Date();
      const vu = item.valid_until ? new Date(item.valid_until) : null;
      const noBalance = (Number(item.liter_remaining || 0) <= 0) || (Number(item.remaining_amount || 0) <= 0);
      return (vu && vu < now) || noBalance;
    },
    isOngoing(item) {
      const pl = this.r2(item?.purchase_liters);
      const lr = this.r2(item?.liter_remaining);
      return !this.isExpired(item) && pl > 0 && lr < pl && lr > 0;
    },
    isUnused(item) {
      const pl = this.r2(item?.purchase_liters);
      const lr = this.r2(item?.liter_remaining);
      return !this.isExpired(item) && pl > 0 && lr === pl;
    },
    statusLabel(item) {
      if (item && item.status === "pending") return this.$t("En attente");
      if (item && item.status === "rejected") return this.$t("Rejete");
      if (this.isExpired(item)) return this.$t("Expiré");
      if (this.isOngoing(item)) return this.$t("En cours");
      if (this.isUnused(item)) return this.$t("Non utilisé");
      return this.$t("—");
    },
    statusColor(item) {
      if (item && item.status === "pending") return "deep-orange";
      if (item && item.status === "rejected") return "error";
      if (this.isExpired(item)) return "error";
      if (this.isOngoing(item)) return "warning";
      if (this.isUnused(item)) return "success";
      return "primary";
    },

    // --- Actions
    canEdit(item) {
      if (!this.donationsEnabled) return false;
      if (!this.canEditDonation) return false;
      // non modifiable si entamé (règle métier partagée)
      const pl = this.r2(item?.purchase_liters);
      const lr = this.r2(item?.liter_remaining);
      return lr >= pl; // éditable seulement si pas entamé (lr == pl)
    },
    canDelete(item) {
      if (!this.donationsEnabled) return false;
      if (!this.canDeleteDonation) return false;
      // idem
      const pl = this.r2(item?.purchase_liters);
      const lr = this.r2(item?.liter_remaining);
      return lr >= pl;
    },
    canValidate(item) {
      return this.canValidateDonation && item && item.status === "pending";
    },

    askValidate(item) {
      this.validateTarget = item;
      this.validateMode = "approve";
      this.validateDialogIsOpen = true;
    },

    confirmApprove() {
      if (!this.validateTarget) return;
      this.validateMode = "approve";
      this.validateActing = true;
      DonationDetailService.approve(this.validateTarget.id, true)
        .then(updated => {
          const idx = this.items.findIndex(d => d.id === updated.id);
          if (idx >= 0) this.$set(this.items, idx, { ...this.items[idx], ...updated });
          this.validateDialogIsOpen = false;
          this.$store.commit("app/message", {
            msg: this.$t("Don validé"), color: "success",
          });
          this.fetchList();
        })
        .catch(err => {
          const data = err?.response?.data || {};
          const msg = data.detail || data.purchase_amount || this.$t("Échec de la validation");
          this.$store.commit("app/message", { msg, color: "error" });
        })
        .finally(() => { this.validateActing = false; });
    },

    confirmReject() {
      if (!this.validateTarget) return;
      this.validateMode = "reject";
      this.validateActing = true;
      DonationDetailService.approve(this.validateTarget.id, false)
        .then(updated => {
          const idx = this.items.findIndex(d => d.id === updated.id);
          if (idx >= 0) this.$set(this.items, idx, { ...this.items[idx], ...updated });
          this.validateDialogIsOpen = false;
          this.$store.commit("app/message", {
            msg: this.$t("Don rejeté"), color: "info",
          });
          this.fetchList();
        })
        .catch(err => {
          const msg = err?.response?.data?.detail || this.$t("Échec du rejet");
          this.$store.commit("app/message", { msg, color: "error" });
        })
        .finally(() => { this.validateActing = false; });
    },
    tryOpenEdit(item) {
      if (!this.donationsEnabled) {
        this.$store.commit("app/message", {
          msg: this.$t("La gestion des dons est désactivée pour cette société."),
          color: "warning",
        });
        return;
      }
      if (item && !this.canEdit(item)) {
        this.$store.commit("app/message", {
          msg: this.$t("Ce don est entamé et ne peut plus être modifié."),
          color: "warning",
        });
        return;
      }
      this.handleOpenEditDialog(item);
    },
    tryOpenDelete(item) {
      if (!this.donationsEnabled) {
        this.$store.commit("app/message", {
          msg: this.$t("La gestion des dons est désactivée pour cette société."),
          color: "warning",
        });
        return;
      }
      if (!this.canDelete(item)) {
        this.$store.commit("app/message", {
          msg: this.$t("Ce don est entamé et ne peut plus être supprimé."),
          color: "warning",
        });
        return;
      }
      this.handleOpenDeleteDialog(item);
    },
    downloadBadge(item) {
      this.downloadingId = item.id;
      DonationDetailService.getBadge(this, item.id)
        .then((blobUrl) => {
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = `badge_don.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
        })
        .catch(() => {
          this.$store.commit("app/message", {
            msg: this.$t("messages.error.permission_denied"),
            color: "error",
          });
        })
        .finally(() => { this.downloadingId = null; });
    },

    // --- Dialog handlers
    handleOpenEditDialog(item) {
      this.selectedItem = item ? JSON.parse(JSON.stringify(item)) : null;
      this.editDialogIsOpen = true;
      this.editDialogContent = "DonationEditor";
    },
    handleOpenDeleteDialog(item) {
      this.selectedItem = item;
      this.deleteDialogIsOpen = true;
    },
    handleDelete() {
      if (!this.selectedItem) return;
      DonationDetailService.delete(this.selectedItem.id)
        .then(() => this.handleSuccess())
        .catch((error) => {
          const msg = error?.response?.data?.state;
          this.handleError(msg || this.$t("messages.error.operation_failed"));
        });
    },
    handleCancel() {
      this.selectedItem = null;
      this.editDialogIsOpen = false;
      this.editDialogContent = null;
      this.deleteDialogIsOpen = false;
    },
    handleError(message) {
      this.$store.commit("app/message", { msg: message, color: "error" });
    },
    handleSuccess() {
      this.handleCancel();
      this.$nextTick(() => {
        this.fetchList();
        this.$store.commit("app/message", {
          msg: this.$t("messages.success.operation"),
          color: "success",
        });
      });
    },
  },
};
</script>

<style scoped>
/* ===== Container ===== */
.donation-list-container {
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
}

/* ===== Stats ===== */
.stats-section { margin-top: 8px; padding: 10px 0; background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%); border-radius: 16px; }
.stats-wrapper { padding: 0 5px; }
.stats-container { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 20px; }

.stat-card-main {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important;
  border-radius: 20px !important;
  padding: 12px 24px;
  color: white;
  box-shadow: 0 10px 30px rgba(25, 118, 210, 0.3) !important;
  position: relative; overflow: hidden;
}
.stat-icon-wrapper { width: 56px; height: 56px; border-radius: 14px; background: rgba(255, 255, 255, 0.25); display: flex; align-items: center; justify-content: center; }
.stat-label { font-size: 12px; text-transform: uppercase; letter-spacing: 1px; color: rgba(255, 255, 255, 0.9); font-weight: 700; margin-bottom: 8px; }
.stat-value { font-size: 34px; font-weight: 800; line-height: 1; color: white; }
.stat-unit { font-size: 14px; font-weight: 600; color: rgba(255, 255, 255, 0.85); }
.stat-secondary { display: flex; align-items: center; gap: 6px; font-size: 14px; font-weight: 700; color: rgba(255, 255, 255, 0.95); }

.stat-card-product {
  border-radius: 16px !important;
  padding: 12px 20px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid #e3f2fd;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08) !important;
}
.stat-card-product:hover { transform: translateY(-4px); box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12) !important; border-color: #90caf9; }
.stat-icon-wrapper-product { width: 44px; height: 44px; border-radius: 12px; background: linear-gradient(135deg, #ff6f00 0%, #ff8f00 100%); display: flex; align-items: center; justify-content: center; }
.stat-label-product { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: #616161; font-weight: 700; margin-bottom: 6px; }
.stat-value-product { font-size: 24px; font-weight: 800; line-height: 1; margin-bottom: 4px; color: #212121; }
.stat-unit-product { font-size: 12px; font-weight: 600; color: #757575; }
.stat-secondary-product { display: flex; align-items: center; gap: 4px; font-size: 12px; font-weight: 600; padding-top: 10px; border-top: 1px solid #e0e0e0; }

/* ===== Table ===== */
.elegant-table >>> thead { background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%); }
.elegant-table >>> thead th {
  font-weight: 700 !important; font-size: 13px !important; text-transform: uppercase; letter-spacing: 0.5px;
  color: #424242 !important; border-bottom: 2px solid #bdbdbd !important; padding: 12px 12px !important;
}
.elegant-table >>> tbody td { padding: 12px 12px !important; color: #212121; border-bottom: 1px solid #eeeeee !important; }

/* ===== Cards mobile ===== */
.donation-card { border-radius: 12px; box-shadow: 0 6px 18px rgba(16, 24, 40, 0.06); }
.grid-2 { display: grid; grid-template-columns: 1fr 1fr; grid-gap: 8px 16px; }
.cell .label { font-size: 12px; color: #6b7280; }
.cell .value { font-size: 14px; font-weight: 600; }
.cell.full-width { grid-column: span 2; }

/* ===== Mobile stats ===== */
.stat-card-mobile { background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important; border-radius: 16px !important; border: none; }
.stat-card-mobile .v-card__text { color: white !important; }
.stat-label-mobile { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 700; }
.stat-value-mobile { font-size: 26px; font-weight: 800; line-height: 1.2; }
.stat-unit-mobile { font-size: 14px; font-weight: 600; opacity: 0.85; }
.stat-icon-mobile { width: 56px; height: 56px; border-radius: 14px; background: rgba(255, 255, 255, 0.25); display: flex; align-items: center; justify-content: center; backdrop-filter: blur(10px); }

/* ===== Helpers ===== */
.full-width { width: 100%; }

@media (max-width: 960px) {
  .stats-container { grid-template-columns: 1fr; }
}
@media (max-width: 360px) {
  .grid-2 { grid-template-columns: 1fr; }
}
</style>
